import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Grid, Box, Container, Typography } from '@material-ui/core';
import i18n from 'i18n-js';
import moment from 'moment';
import { usePrevious } from 'hooks';
import useStyles from './Rulesets.styled';
import { EditableTable } from 'components/UI';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';

const Rulesets = ({ currentGroup }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const prevCurrentGroup = usePrevious(currentGroup?.id);
  const { rules } = useSelector((state) => state?.reservationRulesets);
  const { localeFormat } = useSelector((state) => state.organizations);
  const rulesets = useSelector((state) => state.vehiclesGroups.rrsTimes);

  const handleRuleset = (payload, ruleset, resolve, reject) => {
    dispatch(
      VehiclesGroupsActions.handleReservationRulesetToVehiclesGroups(
        currentGroup?.id,
        ruleset,
        payload,
        resolve,
        reject
      )
    );
  };

  const deleteRuleset = (ruleset, resolve, reject) => {
    dispatch(
      VehiclesGroupsActions.fetchdeleteReservationRulesetFromVehiclesGroups(
        currentGroup?.id,
        ruleset,
        resolve,
        reject
      )
    );
  };

  const shouldHideEditableActions = data => {
    const todayEndOfDay = moment().endOf('day');
    const startDate = moment(data.start_date);
    return startDate.isBefore(todayEndOfDay);
  }

  const parseTableRow = () =>
    rulesets?.map((val) => ({
      id: val?.id,
      rule_set_id: val?.reservation_rules_set_id,
      start_date: val?.start_time ? moment(val?.start_time).format(localeFormat) : '',
      end_date: val?.end_time ? moment(val?.end_time).format(localeFormat) : '',
    }));

  useEffect(() => {
    if (currentGroup?.id && prevCurrentGroup !== currentGroup?.id) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroupsRRStimes(currentGroup?.id));
    }
  }, [currentGroup, prevCurrentGroup, dispatch]);

  const parsedRuleName = rules.reduce((obj, val) => {
    obj[val.id] = val.name;
    return obj;
  }, {});

  if (rulesets) {
    rulesets.forEach((val) => {
      const id = val?.reservation_rules_set?.id;
      if (id && !parsedRuleName[id]) {
        parsedRuleName[id] = val?.reservation_rules_set?.name;
      }
    });
  }

  const columns = [
    {
      title: i18n.t('common.ruleset_name'),
      field: 'rule_set_id',
      lookup: parsedRuleName,
      dataCy: 'ruleSelect',
    },
    {
      title: i18n.t('common.start_date'),
      field: 'start_date',
      editComponent: (props) => {
        return (
          <KeyboardDatePicker
            id="date-start"
            label="Start date"
            format="MM/DD/YYYY"
            className="material-datepicker"
            minDate={moment().add(1, 'days').startOf('day').toDate()}
            value={props.value ? moment(new Date(props.value)) : null}
            onChange={props.onChange}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
          />
        );
      },
    },
    {
      title: i18n.t('common.end_date'),
      field: 'end_date',
      editComponent: (props) => {
        return (
          <KeyboardDatePicker 
            id="date-end"
            label="End date"
            format="MM/DD/YYYY"
            className="material-datepicker"
            minDate={new Date()}
            value={props.value ? moment(new Date(props.value)) : null}
            onChange={props.onChange}
            KeyboardButtonProps={{
              'aria-label': 'change date',
            }}
          />
        );
      },
    },
  ];

  return (
    <Box className={classes.container}>
      <Container className="h-100">
        <Grid container direction="row" alignItems="center" justifyContent="center">
          <Grid item xs={12}>
            <Typography gutterBottom component="div">
              <Box fontWeight="fontWeightMedium" fontSize={14} my={2}>
                {i18n.t('vehicle_groups.reservations_rulesets')}
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <EditableTable
              addButtonProps={{
                variant: 'outlined',
                position: 'flex-end',
                dataCy: 'add-new-rrs',
                label: i18n.t('pricing_rules.add_new_rule'),
              }}
              requiredFields={['rule_set_id']}
              data={parseTableRow() || []}
              isEditHidden={shouldHideEditableActions}
              isDeleteHidden={shouldHideEditableActions}
              columns={columns || []}
              onRowAdd={(values, resolve, reject) => {
                let payload = {
                  rule_set_id: parseInt(values.rule_set_id),
                };

                if (values.start_date) {
                  payload.start_date = moment(values.start_date).format('YYYY-MM-DD');
                }

                if (values.end_date) {
                  payload.end_date = moment(values.end_date).format('YYYY-MM-DD');
                }
                handleRuleset(payload, null, resolve, reject);
              }}
              onDelete={(values, resolve, reject) => {
                deleteRuleset(values.id, resolve, reject);
              }}
              onEdit={(values, resolve, reject) => {
                let payload = {
                  new_rule_set_id: parseInt(values.rule_set_id),
                };

                if (values.start_date) {
                  payload.start_date = moment(values.start_date).format('YYYY-MM-DD');
                }

                if (values.end_date) {
                  payload.end_date = moment(values.end_date).format('YYYY-MM-DD');
                }
                handleRuleset(payload, values.id, resolve, reject);
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Rulesets;
